<template>
  <vee-form v-slot="{ validate, errors, setFieldError }" as="" :initial-errors="initialErrors">
    <div class="standard-form item-more-info">
      <!-- xhr error state -->
      <callout v-if="error" type="error">
        <div>{{ error }}</div>
      </callout>

      <!-- purchase options toggle -->
      <div v-if="isPurchaseGift" class="gift-purchase-options group -flex -between">
        <div class="option-text">Purchase:</div>
        <div class="option-button">
          <icon-button
            :reduce="false"
            :classes="`-ico-txt -no-case ${selfPurchaseClasses}`"
            :disabled="!selfActionEnabled"
            icon="person"
            label="For Myself"
            value="self"
            @click-action="handlePurchaseOption"
          ></icon-button>
        </div>
        <div class="option-button">
          <icon-button
            :reduce="false"
            :classes="`-ico-txt -no-case ${formData.is_gift ? '-dark' : '-neutral'}`"
            icon="redeem"
            label="As a Gift"
            value="gift"
            :aria-expanded="formData.is_gift.toString()"
            :aria-controls="expandedId"
            aria-label="Expand Gift Message Form"
            @click-action="handlePurchaseOption"
          ></icon-button>
        </div>
      </div>

      <div
        v-if="formData.is_gift"
        :id="expandedId"
        ref="giftForm"
        class="gift-message-form"
        :aria-hidden="!formData.is_gift"
      >
        <text-input
          v-model="formData.email"
          :input-id="getUniqueFieldName(formFields.email)"
          type="email"
          label="Recipient Email Address"
          :show-label="false"
          placeholder="Recipient Email Address"
          rules="required|email|max:120"
          validate-as="Recipient email"
          maxlength="120"
          autocomplete="off"
          data-hj-suppress
          @change="handleFieldUpdate('email', validate, setFieldError)"
        />

        <textarea-input
          v-model="formData.message"
          :input-id="getUniqueFieldName(formFields.message)"
          label="Gift Message"
          :show-label="false"
          placeholder="Add a Message"
          maxlength="500"
          @change="handleFieldUpdate('message', validate, setFieldError)"
        />

        <checkbox-input
          v-model="formData.anonymous"
          :input-id="getUniqueFieldName(formFields.anonymous)"
          label="Make this gift anonymous."
          @change="handleFieldUpdate('anonymous', validate, setFieldError)"
        />
      </div>

      <!-- pay it forward -->
      <div v-if="isPayForward" :id="expandedId" ref="giftForm" class="gift-message-form">
        <div class="option-text">Why are you paying it forward? (Optional)</div>
        <textarea-input
          v-model="formData.message"
          :input-id="getUniqueFieldName(formFields.message)"
          label="Why are you paying it forward? (Optional)"
          :show-label="false"
          placeholder="Short Message"
          maxlength="500"
          @change="handleFieldUpdate('message', validate, setFieldError)"
        />
      </div>

      <!-- error state -->
      <div v-if="isPurchaseGift && !selfActionEnabled" class="gift-message-note">
        <!-- is_gift is not an input, so manually show server error -->
        <callout
          v-if="!formData.is_gift && errors && errors[getUniqueFieldName('is_gift')]"
          type="error"
        >
          <div>{{ errors[getUniqueFieldName('is_gift')] }}</div>
        </callout>

        <p v-else class="body -tight -note">
          You cannot purchase or activate FB Plus Passes for yourself while on an FB Plus auto-renew
          subscription.
        </p>
      </div>
    </div>
  </vee-form>
</template>

<script setup lang="ts">
/**
 * Powers purchase as gift form and free gift form
 * [x] - client side validation
 * [x] - shows server side validation if 4xx on PUT (email)
 * [x] - field level saves on blur
 * [x] - cannot purchase for self if selfActionEnabled is disabled
 * [x] - shows error bag errors if coming redirect back from checkout step
 * - can decline free gift
 */
import { computed, nextTick, onBeforeMount, ref } from 'vue'
import { Form as VeeForm, type PrivateFormContext } from 'vee-validate'
import { initExtraValidationRules, errorBagToInitialErrors } from '@/utils/forms/validation-rules'
import TextInput from '@/components/forms/text-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import Callout from '@/components/notifications/callout.vue'
import { fbApi, FbApiError } from '@/utils/http-api'
import TextareaInput from '@/components/forms/textarea-input.vue'
import type { ErrorBag } from '@/types/global-types'
import type { GiftFormData } from '@/types/model-types'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    errorBag?: ErrorBag | null
    initFormData: GiftFormData
    cartItemId: string
    selfActionEnabled: boolean
    // purchaseGift = user going to buy and give as a gift (EX 90-day pass)
    // freeGift = item for free and give as a gift (EX: 1 day pass free with a 90-day pass)
    formType?: 'purchaseGift' | 'freeGift' | 'payForward'

    // focus on mounted
    // used on free gift forms as the toggle is in free-gift-product.vue
    initFocusInput?: boolean
  }>(),
  {
    errorBag: null,
    formType: 'purchaseGift',
    initFocusInput: false,
  },
)

/** STATE **/
const giftForm = ref<HTMLElement | null>(null)
const error = ref('')
const formData = ref<GiftFormData>({
  ...props.initFormData,
})

/** COMPUTED **/
const isPurchaseGift = computed(() => props.formType === 'purchaseGift')
const isPayForward = computed(() => props.formType === 'payForward')
const formFields = computed(() => {
  // map to gift_* fields
  if (isPurchaseGift.value) {
    return {
      is_gift: 'is_gift',
      email: 'gift_email',
      message: 'gift_message',
      anonymous: 'gift_anonymous',
      decline: '',
    }
  }

  // isPayForward shares gift_message field, utilizes no other fields
  if (isPayForward.value) {
    return {
      is_gift: '',
      email: '',
      message: 'gift_message',
      anonymous: '',
      decline: '',
    }
  }

  // map to free_gift_* fields for free_gift_product_id
  return {
    is_gift: '',
    email: 'free_gift_email',
    message: 'free_gift_message',
    anonymous: 'free_gift_anonymous',
    decline: 'free_gift_decline',
  }
})

const initialErrors = computed(() => errorBagToInitialErrors(props.errorBag))
const expandedId = computed(() => `gift-expand-${props.cartItemId}`)
const selfPurchaseClasses = computed(() => {
  if (props.selfActionEnabled) {
    return !formData.value.is_gift ? '-dark' : ''
  }

  // cannot self purchase
  return '-disabled -light'
})

/** LIFECYCLE **/
onBeforeMount(() => {
  initExtraValidationRules()

  if (props.initFocusInput) {
    focusFirstInput()
  }
})

/** METHODS **/
// could have multiple on a cart at once, so cannot just use "email" as the input id
function getUniqueFieldName(field: string) {
  return `${field}_${props.cartItemId}`
}

// no action with payForward, only one textarea, no need to focus
function focusFirstInput() {
  nextTick(() => {
    if (giftForm.value && giftForm.value instanceof HTMLElement) {
      const input = giftForm.value.querySelector('input')

      if (input) {
        input.focus()
      }
    }
  })
}

async function handlePurchaseOption(e: InputEvent) {
  formData.value.is_gift = false

  if (e.currentTarget instanceof window.HTMLButtonElement && e.currentTarget.value === 'gift') {
    formData.value.is_gift = true

    // focus first input on form
    focusFirstInput()
  }

  await handleFieldUpdate('is_gift')
}

async function handleFieldUpdate(
  field: keyof GiftFormData,
  validator?: PrivateFormContext['validate'],
  setFieldError?: PrivateFormContext['setFieldError'],
) {
  error.value = ''
  const cartField = formFields.value[field]
  const uniqueField = getUniqueFieldName(cartField)

  // make sure vue models are synced up
  await nextTick()

  try {
    // only validate if validator was passed in, EX: is_gift doesn't validate
    // currently only email field validates
    if (validator) {
      const results = await validator()

      // if email, save if completely cleared OR valid only
      if (field === 'email' && formData.value[field] && !results.valid) {
        return
      }
    }

    // field level saves
    await fbApi.put(`/store/cart`, {
      json: {
        id: props.cartItemId,
        [cartField]: formData.value[field],
      },
      // self-handled w/ message
      skipErrorModal: true,
    })
  } catch (err) {
    // server side validation to vee-validate
    if (err instanceof FbApiError && setFieldError) {
      if (err.status === 400 && err.body?.message === 'Invalid Data') {
        // validation errors
        const errorBag = errorBagToInitialErrors(err.body.data)

        if (errorBag) {
          Object.keys(errorBag).forEach((field) => {
            setFieldError(uniqueField, errorBag[field])
          })
        }
      }
    }
    // end server side validation

    error.value = 'Error saving, please review'
    console.error('Error saving gift message form', err)
  }
}
</script>
